/**
 * Sign in.
 * Route: /auth/login
 * @file src/web/features/auth/pages/login.tsx
 *
 * @llm-rule WHEN: Anyone opens the product signed out
 * @llm-rule NOTE: Split screen — brand poster left, form right, and on a phone
 *   the poster collapses to a slim banner so the form is above the fold.
 * @llm-rule AVOID: Nesting the field component inside the page. Defined inline
 *   it is a new component type on every keystroke, React remounts it, and the
 *   input loses focus after each character.
 */

import React, { useEffect, useState } from 'react';
import { ArrowRight, Eye, EyeOff, Loader2, Lock, Mail, ScanFace, ShieldAlert } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { PRODUCT } from '../../../shared/brand';
import { useAuth } from '../hooks';
import { AuthBrandPanel } from '../components/AuthBrandPanel';

/* -------------------------------------------------------------------------- */
/* Field — MODULE level. See the file header for why.                         */
/* -------------------------------------------------------------------------- */

function Field({
  id,
  label,
  icon: Icon,
  trailing,
  ...input
}: React.InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  icon: typeof Mail;
  trailing?: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-[12px] font-medium tracking-[0.01em] text-ink-soft"
      >
        {label}
      </label>
      <div className="group relative">
        <Icon
          className="pointer-events-none absolute left-3.5 top-1/2 size-[16px] -translate-y-1/2 text-ink-ghost transition-colors group-focus-within:text-brand"
          strokeWidth={1.8}
        />
        <input
          id={id}
          {...input}
          className={`h-11 w-full rounded-xl border border-rule bg-paper pl-10 text-[14px] text-ink transition-all placeholder:text-ink-ghost hover:border-ink-ghost/60 focus:border-brand focus:outline-none ${
            trailing ? 'pr-11' : 'pr-4'
          }`}
          style={{ boxShadow: 'inset 0 1px 2px oklch(0.21 0.021 258 / 0.04)' }}
        />
        {trailing}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  // Clear error on successful auth
  useEffect(() => {
    if (isAuthenticated) {
      setLoginError(null);
    }
  }, [isAuthenticated]);

  // Show loading screen only on initial auth check, not during login process
  if (isLoading && !loginError && !formData.email && !formData.password) {
    return (
      <div className="grid min-h-screen place-items-center bg-paper-warm">
        <div className="flex flex-col items-center gap-3">
          <div className="grid size-11 place-items-center rounded-xl" style={{ background: 'var(--brand-ramp)' }}>
            <ScanFace className="size-5 text-white" strokeWidth={1.8} />
          </div>
          <div className="flex items-center gap-2 text-[13px] text-ink-faint">
            <Loader2 className="size-4 animate-spin" />
            Loading…
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent double submission
    if (isSubmitting) {
      return false;
    }

    // Clear previous errors
    setLoginError(null);
    setIsSubmitting(true);

    try {
      const result = await login({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe,
      });

      if (!result.success) {
        setLoginError('Invalid username or password');
      }
    } catch (error: any) {
      setLoginError(error.message || 'Network error occurred');
    } finally {
      setIsSubmitting(false);
    }

    return false; // Explicitly prevent any form submission behavior
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <>
      <SEO
        title="Sign in"
        description={`${PRODUCT.name} — ${PRODUCT.tagline}`}
        keywords="login, sign in, attendance, face verification"
      />

      <div className="flex min-h-screen bg-paper">
        <AuthBrandPanel />

        {/* ------------------------------ Form side ------------------------------ */}
        <div className="flex flex-1 flex-col">
          {/* On a phone the poster is gone, so the brand arrives as a slim band. */}
          <div className="aurora flex items-center gap-3 px-6 py-5 lg:hidden">
            <div className="relative z-10 grid size-9 place-items-center rounded-[11px] bg-white/10 ring-1 ring-inset ring-white/25">
              <ScanFace className="size-[19px] text-white" strokeWidth={1.8} />
            </div>
            <div className="relative z-10 leading-tight">
              <div className="text-[16px] font-semibold tracking-tight text-white">
                {PRODUCT.name}
              </div>
              <div className="text-[11.5px] text-white/50">{PRODUCT.tagline}</div>
            </div>
          </div>

          <div className="flex flex-1 items-center justify-center px-6 py-10 sm:px-10">
            <div className="w-full max-w-[368px]">
              <div className="anim-rise">
                <h1 className="text-[27px] font-semibold leading-tight tracking-[-0.02em] text-ink">
                  Sign in
                </h1>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-soft">
                  Welcome back. Your day starts at the clock-in screen.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="anim-rise mt-7 flex flex-col gap-4"
                style={{ ['--d' as string]: '80ms' }}
              >
                {loginError && (
                  <div
                    role="alert"
                    className="flex items-start gap-2.5 rounded-xl px-3.5 py-3"
                    style={{
                      background: 'var(--state-bad-bg)',
                      border: '1px solid var(--state-bad-border)',
                    }}
                  >
                    <ShieldAlert className="mt-px size-4 flex-none text-flag" strokeWidth={1.9} />
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-semibold text-ink">
                        That didn’t sign you in
                      </div>
                      <div className="mt-0.5 text-[12px] leading-relaxed text-ink-soft">
                        {loginError}. Check the address and try again — a higher level can reset
                        your password.
                      </div>
                    </div>
                  </div>
                )}

                <Field
                  id="email"
                  name="email"
                  label="Email address"
                  icon={Mail}
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="you@company.com"
                />

                <Field
                  id="password"
                  name="password"
                  label="Password"
                  icon={Lock}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  trailing={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-2 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-lg text-ink-ghost transition-colors hover:bg-paper-sunk hover:text-ink-soft"
                    >
                      {showPassword ? (
                        <EyeOff className="size-[16px]" strokeWidth={1.8} />
                      ) : (
                        <Eye className="size-[16px]" strokeWidth={1.8} />
                      )}
                    </button>
                  }
                />

                <div className="flex items-center justify-between">
                  <label
                    htmlFor="rememberMe"
                    className="flex cursor-pointer select-none items-center gap-2 text-[12.5px] text-ink-soft"
                  >
                    <input
                      id="rememberMe"
                      name="rememberMe"
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={handleInputChange}
                      className="size-[15px] cursor-pointer rounded-[5px] border-rule accent-[var(--brand)]"
                    />
                    Keep me signed in
                  </label>

                  <a
                    href="/auth/forgot-password"
                    className="text-[12.5px] font-medium text-brand underline-offset-4 transition-colors hover:underline"
                  >
                    Forgot password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group mt-1 flex h-11 w-full items-center justify-center gap-2 rounded-xl text-[14px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-65"
                  style={{ background: 'var(--brand-ramp)', boxShadow: 'var(--elev-2)' }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight
                        className="size-4 transition-transform duration-200 group-hover:translate-x-0.5"
                        strokeWidth={2.2}
                      />
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <div
                className="anim-rise mt-8 border-t border-rule pt-5"
                style={{ ['--d' as string]: '160ms' }}
              >
                <p className="text-[12.5px] leading-relaxed text-ink-faint">
                  People are added to a company by its Company Admin or Head. Setting up a new company?{' '}
                  <a
                    href="/auth/register"
                    className="font-medium text-brand underline-offset-4 hover:underline"
                  >
                    Create an account
                  </a>
                  .
                </p>
                <p className="mt-3 flex items-center gap-1.5 text-[11.5px] text-ink-ghost">
                  <ScanFace className="size-3.5" strokeWidth={1.7} />
                  You enrol once. Every clock-in is matched against that photo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
